<script lang="ts">
import type { CommentsCopy } from "$lib/i18n/copy/comments";
import type { FieldError } from "$lib/server/blog-comments";

// The comment form, the same under an article and on its comments page. It is
// a plain HTML form posting to the comments page: it works without
// JavaScript, and an accepted comment lands the visitor on the page that says
// what happens next.
//
// The `website` field is the trap for form-filling scripts. It is moved off
// screen and out of the tab order and the accessibility tree, so a person
// never fills it, and the API files anything that arrives with it filled as
// spam without telling the sender.
interface Props {
  copy: CommentsCopy;
  action: string;
  name?: string;
  text?: string;
  errors?: readonly FieldError[];
  problem?: "rate_limited" | "failed" | null;
}

let { copy, action, name = "", text = "", errors = [], problem = null }: Props = $props();

const MESSAGES: Record<FieldError, keyof CommentsCopy> = {
  name_missing: "nameMissing",
  name_too_long: "nameTooLong",
  text_too_short: "textTooShort",
  text_too_long: "textTooLong",
};

const nameError = $derived(errors.find((error) => error.startsWith("name_")));
const textError = $derived(errors.find((error) => error.startsWith("text_")));
</script>

<form method="POST" {action} class="mt-4 space-y-4" data-testid="comment-form">
  {#if problem !== null}
    <p role="alert" class="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
      {problem === "rate_limited" ? copy.rateLimited : copy.failed}
    </p>
  {/if}

  <div>
    <label for="comment-name" class="block text-sm font-medium text-slate-700">{copy.nameLabel}</label>
    <input
      id="comment-name"
      name="name"
      type="text"
      required
      maxlength="60"
      autocomplete="nickname"
      value={name}
      aria-invalid={nameError !== undefined}
      aria-describedby={nameError !== undefined ? "comment-name-error" : undefined}
      class="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
    />
    {#if nameError !== undefined}
      <p id="comment-name-error" class="mt-1 text-sm text-red-700">{copy[MESSAGES[nameError]]}</p>
    {/if}
  </div>

  <div>
    <label for="comment-text" class="block text-sm font-medium text-slate-700">{copy.textLabel}</label>
    <textarea
      id="comment-text"
      name="text"
      required
      minlength="3"
      maxlength="2000"
      rows="5"
      aria-invalid={textError !== undefined}
      aria-describedby={textError !== undefined ? "comment-text-hint comment-text-error" : "comment-text-hint"}
      class="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
      >{text}</textarea
    >
    <p id="comment-text-hint" class="mt-1 text-xs text-slate-500">{copy.textHint}</p>
    {#if textError !== undefined}
      <p id="comment-text-error" class="mt-1 text-sm text-red-700">{copy[MESSAGES[textError]]}</p>
    {/if}
  </div>

  <div class="trap" aria-hidden="true">
    <label for="comment-website">{copy.honeypotLabel}</label>
    <input id="comment-website" name="website" type="text" tabindex="-1" autocomplete="off" />
  </div>

  <button
    type="submit"
    class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
    >{copy.submit}</button
  >
</form>

<style>
  .trap {
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
</style>
